import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ZoomIn, ZoomOut, RotateCw, Download, Maximize2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';

interface ImageRendererProps {
  artifact: Artifact;
}

export function ImageRenderer({ artifact }: ImageRendererProps) {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const getImageSrc = () => {
    if (typeof artifact.data === 'string') {
      // Check if it's a base64 data URL
      if (artifact.data.startsWith('data:')) {
        return artifact.data;
      }
      // If it's base64 without data URL prefix, add it
      return `data:${artifact.mimeType};base64,${artifact.data}`;
    } else if (artifact.data instanceof ArrayBuffer) {
      // Convert ArrayBuffer to base64
      const bytes = new Uint8Array(artifact.data);
      const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
      const base64 = btoa(binary);
      return `data:${artifact.mimeType};base64,${base64}`;
    }
    return '';
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 500));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = getImageSrc();
    link.download = artifact.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const imageSrc = getImageSrc();

  if (!imageSrc) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Unable to display image. Invalid image data format.
        </AlertDescription>
      </Alert>
    );
  }

  if (hasError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load image. The image data may be corrupted.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 25}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Badge variant="secondary" className="min-w-[60px] justify-center">
            {zoom}%
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 500}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRotate}
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Maximize2 className="h-4 w-4" />
                Full Screen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
              <div className="flex items-center justify-center p-4 h-full">
                <img
                  src={imageSrc}
                  alt={artifact.name}
                  className="max-w-full max-h-full object-contain"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </div>

      {/* Image Display */}
      <div className="border rounded-lg overflow-hidden bg-muted/30">
        <div className="flex items-center justify-center p-4 min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          <img
            src={imageSrc}
            alt={artifact.name}
            className="max-w-full h-auto object-contain transition-transform duration-200"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              display: isLoading ? 'none' : 'block',
            }}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
          />
        </div>
      </div>

      {/* Image Info */}
      <div className="text-xs text-muted-foreground space-y-1">
        <div>Type: {artifact.mimeType}</div>
        {artifact.metadata?.width && artifact.metadata?.height && (
          <div>
            Dimensions: {artifact.metadata.width} × {artifact.metadata.height}
          </div>
        )}
        {artifact.metadata?.size && (
          <div>Size: {(Number(artifact.metadata.size) / 1024).toFixed(1)} KB</div>
        )}
      </div>
    </div>
  );
}